import React, { useMemo, useState } from 'react';
import CPPUtils from 'services/CPPUtils';
import { HISTORY_TYPES } from 'components/HealthRecords/AppointmentNotes/NewAppointmentNote/constants';
import { DISEASES } from 'components/HealthRecords/Immunizations/YellowCard/view';
import _ from 'lodash';
import CalendarUtils from 'services/CalendarUtils';
import moment from 'moment-timezone';
import PrefilledTemplates from 'components/HealthRecords/Documents/DocumentTemplate/PrefilledTemplates';
import DocumentUtils from 'services/DocumentUtils';
import PatientUtils from 'services/PatientUtils';
import { getHealthCardLabel } from 'services/portalData';
import { MinistryOHIPGenderEnum } from 'generated/api';
import PhysicianUtils from 'services/PhysicianUtils';
import DefaultButton from 'components/Utils/NewDefaultButton';
import { useAppSelector } from 'careand-redux/store';
import DocumentTemplate from 'components/HealthRecords/Documents/DocumentTemplate';
import { api } from 'careand-redux/api/ApiThunks';
import { useAppDispatch } from 'careand-redux/useAppDispatch';

export const FLOW_INPUTS = {
  text: 'text',
  switch: 'switch',
  textarea: 'textarea',
  checkbox: 'checkbox',
  medications: 'medications',
  multiselect: 'multiselect',
};

export const Flows = {
  physical_assessment: {
    key: 'physical_assessment',
    icon: 'person',
    label: 'Physical Assessment',
    includeBeautify: true,
    summary: true,
    alwaysIncludeFirstPage: true,
    pages: ({ healthRecords }) => [
      {
        title: 'Goals & Concerns',
        hideTitle: true,
        questions: [
          {
            label: 'Goals/Concerns',
          },
        ],
      },
      {
        title: 'Medical History',
        questions: [
          {
            label: 'Allergies',
            defaultAnswer: CPPUtils.getActiveHealthRecords(
              healthRecords?.healthRecords,
              'allergies',
            ).join(', '),
          },
          {
            label: 'Active Issues',
            defaultAnswer: CPPUtils.getActiveHealthRecords(
              healthRecords?.healthRecords,
              'active_issues',
            ).join(', '),
          },
          {
            label: 'Current medical conditions',
            defaultAnswer: CPPUtils.getActiveHealthRecords(
              healthRecords?.healthRecords,
              'medical_issues',
              (value) => value.issue_type === 'Current Issues',
            ).join(', '),
          },
          {
            label: 'Past medical conditions',
            defaultAnswer: CPPUtils.getActiveHealthRecords(
              healthRecords?.healthRecords,
              'medical_issues',
              (value) => value.issue_type === 'Past Issues',
            ).join(', '),
          },
          {
            label: 'Surgical History',
            defaultAnswer: CPPUtils.getActiveHealthRecords(
              healthRecords?.healthRecords,
              'history',
              (value) => value.history_type === HISTORY_TYPES.surgical_history,
              ({ name, details }) => (details ? `${name} - ${details}` : name),
            ).join(', '),
          },
          {
            label: 'Medications',
            questions: [
              {
                input: FLOW_INPUTS.medications,
                hideLabel: true,
              },
            ],
          },
          {
            label: 'Vitamins/Supplements',
          },
          {
            label: 'Family History',
            noteLabel: 'FamHx',
            defaultAnswer: CPPUtils.getActiveHealthRecords(
              healthRecords?.healthRecords,
              'history',
              (value) => value.history_type === HISTORY_TYPES.family_history,
              (value) => `${value.name} - ${value.details}`,
            ).join(', '),
          },
        ],
      },
      {
        title: 'Vaccination Dates',
        questions: [
          {
            label: 'Immunization summary deferred',
            input: FLOW_INPUTS.checkbox,
            skipPage: true,
            noAnswerText: true,
          },
          {
            label: 'Last Td',
            instructions: 'q10y',
            questions: [
              {
                input: FLOW_INPUTS.checkbox,
                label: 'Completed?',
                defaultAnswer: lastImmunizationDate(healthRecords.immunizations, 'Tetanus')
                  ? moment().diff(
                      moment(lastImmunizationDate(healthRecords.immunizations, 'Tetanus')),
                      'years',
                    ) <= 10 || undefined
                  : undefined,
              },
              {
                input: FLOW_INPUTS.checkbox,
                label: 'Receiving today?',
              },
              {
                input: FLOW_INPUTS.textarea,
                hideLabel: true,
              },
            ],
          },
          {
            label: 'Shingrix',
            instructions: '> 50 y/o',
            questions: [
              {
                input: FLOW_INPUTS.checkbox,
                label: 'Completed?',
                defaultAnswer:
                  getMatchingImmunizations(healthRecords.immunizations, 'Shingles')?.length >= 2 ||
                  undefined,
              },
              {
                input: FLOW_INPUTS.checkbox,
                label: 'Receiving today?',
              },
              {
                input: FLOW_INPUTS.textarea,
                hideLabel: true,
              },
            ],
          },
          {
            label: 'Pneumococcal',
            instructions: '> 65 y/o',
            questions: [
              {
                input: FLOW_INPUTS.checkbox,
                label: 'Received?',
                defaultAnswer:
                  !!lastImmunizationDate(healthRecords.immunizations, undefined, [
                    'ppv23',
                    'pvc13',
                  ]) || undefined,
              },
              {
                input: FLOW_INPUTS.checkbox,
                label: 'Receiving today?',
              },
              {
                input: FLOW_INPUTS.textarea,
                hideLabel: true,
              },
            ],
          },
          {
            label: 'Prevnar 20',
            instructions: 'risk dependent',
            questions: [
              {
                input: FLOW_INPUTS.checkbox,
                label: 'Received?',
                defaultAnswer:
                  !!lastImmunizationDate(healthRecords.immunizations, undefined, [
                    'pvc20',
                    'pneu-c-20',
                  ]) || undefined,
              },
              {
                input: FLOW_INPUTS.checkbox,
                label: 'Receiving today?',
              },
              {
                input: FLOW_INPUTS.textarea,
                hideLabel: true,
              },
            ],
          },
          {
            label: 'Gardasil-9',
            instructions: 'received in school (up to 45 y/o w/ risks)',
            questions: [
              {
                input: FLOW_INPUTS.checkbox,
                label: 'Completed?',
                defaultAnswer:
                  getMatchingImmunizations(healthRecords.immunizations, 'HPV')?.length >= 3 ||
                  undefined,
              },
              {
                input: FLOW_INPUTS.checkbox,
                label: 'Receiving today?',
              },
              {
                input: FLOW_INPUTS.textarea,
                hideLabel: true,
              },
            ],
          },
          {
            label: 'Annual Flu',
            questions: [
              {
                input: FLOW_INPUTS.checkbox,
                label: 'Received?',
                defaultAnswer: lastImmunizationDate(healthRecords.immunizations, 'Influenza')
                  ? moment().diff(
                      moment(lastImmunizationDate(healthRecords.immunizations, 'Influenza')),
                      'days',
                    ) <= 365 || undefined
                  : undefined,
              },
              {
                input: FLOW_INPUTS.checkbox,
                label: 'Receiving today?',
              },
              {
                input: FLOW_INPUTS.textarea,
                hideLabel: true,
              },
            ],
          },
        ],
      },
      {
        title: 'Health Maintenance and Screening',
        questions: [
          {
            label: 'Eye exam',
          },
          {
            label: 'Dental exam/cleaning',
          },
          {
            label: 'PAP + results',
            instructions: 'q3y, >25 y/o',
            filters: {
              gender: 'Female',
            },
          },
          {
            label: 'FIT/Colonoscopy Age',
            instructions: '>50 y/o',
          },
          {
            label: 'Mammogram',
            instructions: 'q2y >40 y/o',
            filters: {
              gender: 'Female',
            },
          },
          {
            label: 'BMD',
            instructions: '50-65 + at-risk, > 65 y/o',
          },
          {
            label: 'LDCT for lung cancer once',
            instructions: '>20pack/year + >50 y/o',
          },
        ],
      },
      {
        title: 'Social History',
        questions: [
          {
            label: 'Alcohol',
            defaultAnswer: CPPUtils.getActiveHealthRecords(
              healthRecords?.healthRecords,
              'lifestyle_factors',
              (value) => value.name === 'Alcohol',
              (value) => value.details,
            ).join(', '),
          },
          {
            label: 'Smoking',
            defaultAnswer: CPPUtils.getActiveHealthRecords(
              healthRecords?.healthRecords,
              'lifestyle_factors',
              (value) => value.name === 'Smoking',
              (value) => value.details,
            ).join(', '),
          },
          {
            label: 'Nicotine',
            defaultAnswer: CPPUtils.getActiveHealthRecords(
              healthRecords?.healthRecords,
              'lifestyle_factors',
              (value) => value.name === 'Nicotine',
              (value) => value.details,
            ).join(', '),
          },
          {
            label: 'Drugs',
            defaultAnswer: CPPUtils.getActiveHealthRecords(
              healthRecords?.healthRecords,
              'lifestyle_factors',
              (value) => value.name === 'Drugs',
              (value) => value.details,
            ).join(', '),
          },
          {
            label: 'Diet',
            defaultAnswer: CPPUtils.getActiveHealthRecords(
              healthRecords?.healthRecords,
              'lifestyle_factors',
              (value) => value.name === 'Diet',
              (value) => value.details,
            ).join(', '),
          },
          {
            label: 'Exercise',
            defaultAnswer: CPPUtils.getActiveHealthRecords(
              healthRecords?.healthRecords,
              'lifestyle_factors',
              (value) => value.name === 'Exercise',
              (value) => value.details,
            ).join(', '),
          },
          {
            label: 'Occupation/school',
            defaultAnswer: CPPUtils.getActiveHealthRecords(
              healthRecords?.healthRecords,
              'lifestyle_factors',
              (value) => value.name === 'Employment',
              (value) => value.details,
            ).join(', '),
          },
          {
            label: 'Living',
          },
          {
            label: 'Pets',
            defaultAnswer: CPPUtils.getActiveHealthRecords(
              healthRecords?.healthRecords,
              'lifestyle_factors',
              (value) => value.name === 'Pets',
              (value) => value.details,
            ).join(', '),
          },
          {
            label: 'Sexuality/Relationships',
            instructions: 'x partners in last 6 months, male/female/both',
          },
          {
            label: 'Supports/Stress levels',
          },
          {
            label: 'Sleep',
            defaultAnswer: CPPUtils.getActiveHealthRecords(
              healthRecords?.healthRecords,
              'lifestyle_factors',
              (value) => value.name === 'Sleep',
              (value) => value.details,
            ).join(', '),
          },
        ],
      },
      {
        title: 'Gynecologic History',
        filters: {
          gender: 'Female',
        },
        questions: [
          {
            label: 'G T P A L / nulliparous',
          },
          {
            label: 'Family planning attitudes',
          },
          {
            label: 'Contraception',
          },
          {
            label: 'Date of LMP',
          },
          {
            label: 'Concerns about period?',
            filterLabel: 'period_concerns',
            input: FLOW_INPUTS.switch,
            inputOptions: {
              defaultValue: false,
            },
          },
          {
            label: 'Menarche',
            filters: {
              period_concerns: true,
            },
          },
          {
            label: 'Frequency',
            instructions: '24-38d normal',
            filters: {
              period_concerns: true,
            },
          },
          {
            label: 'Regularity',
            instructions: '7-9d variation normal',
            filters: {
              period_concerns: true,
            },
          },
          {
            label: 'Duration',
            instructions: '<8d normal',
            filters: {
              period_concerns: true,
            },
          },
          {
            label: 'Flow volume',
            instructions:
              'how often to change pads (>3hr N), # pads for period (<21 N), clot size (<1" N), change pad overnight (seldom N)',
            filters: {
              period_concerns: true,
            },
          },
          {
            label: 'Intermenstrual bleeding/spotting',
            filters: {
              period_concerns: true,
            },
          },
          {
            label: 'Unscheduled bleeding if on BC',
            filters: {
              period_concerns: true,
            },
          },
        ],
      },
      {
        title: 'Metrics',
        questions: [
          {
            label: 'Heart Rate',
          },
          {
            label: 'Blood Pressure (sitting)',
          },
          {
            label: 'O2',
          },
          {
            label: 'Temperature',
          },
        ],
      },
      {
        title: 'Objective Assessment',
        noteTitle: 'O/',
        questions: [
          {
            label: 'Objective assessment deferred',
            input: FLOW_INPUTS.checkbox,
            skipPage: true,
            noAnswerText: true,
          },
          {
            label: 'Gen',
            defaultAnswer: 'A&O, well kempt, appears stable',
          },
          {
            title: 'HEENT:',
          },
          {
            label: 'Head',
            defaultAnswer:
              'normocephalic, hair normal amount and texture, no scalp lesions or tenderness',
          },
          {
            label: 'Eyes',
            defaultAnswer:
              'sclera white, conjunctiva pink; fundoscopy- red reflex present, no hemorrhages or exudates',
          },
          {
            label: 'Ears',
            defaultAnswer:
              'ext ears no tenderness; auditory canals normal; int ears TMs pearly grey, shiny, and translucent',
          },
          {
            label: 'Nose',
            defaultAnswer:
              'airway patent, no discharge, septum midline, inf and middle turbinates normal, frontal sinuses nontender, maxillary sinuses nontender',
          },
          {
            label: 'Oral',
            defaultAnswer:
              'teeth present with good dentition; tongue, gums, and buccal mucosa pink and moist, no lesions, bleeding, erythema, edema, or exudates; tonsils gr. 2, uvula midline, airway patent',
          },
          {
            label: 'Neck',
            defaultAnswer:
              'trachea midline, no goitre, lymph nodes nonpalpable and nontender, normal ROM',
          },
          {
            label: '',
          },
          {
            label: 'Resp',
            defaultAnswer:
              'breathing unlabored, resp expansion symmetrical, good AEB, no adventitia',
          },
          {
            label: 'CV',
            defaultAnswer: 'normal S1/S2, no adventitia or murmurs',
          },
          {
            label: '',
          },
          {
            label: 'GI/GU',
            defaultAnswer:
              'abdo flat, BS normal x4; no tenderness or guarding; no organomegaly, no CVA tenderness',
          },
          {
            label: 'Gyne/Uro/Rectal',
            defaultAnswer: 'to be completed by NP',
          },
          {
            label: 'Integ',
            defaultAnswer:
              'Skin warm and dry; no lesions, bruises, cyanosis, or clubbing; cap refill normal; mole check normal',
          },
          {
            label: 'MSK',
            defaultAnswer: 'Normal neck, shoulder, elbow, hip, knee, and ankle ROM',
          },
          {
            label: 'Neuro',
            defaultAnswer:
              'A&Ox3, CNs intact (taste and smell tests deferred), patellar DTRs normal; gait, balance, and coordination normal, tandem walk normal, Romberg neg, pronator drift neg',
          },
        ],
      },
      {
        title: 'Patient Documents',
        actions: (patient) => <SendTemplatesToPatient patient={patient} />,
      },
    ],
  },
  asymptomatic_sti: {
    key: 'asymptomatic_sti',
    icon: 'venus-mars',
    label: 'Asymptomatic STI Assessment',
    summary: false,
    alwaysIncludeFirstPage: true,
    soapSections: {
      O: 'none',
      A: 'Asymptomatic STI Assessment',
      P: `Requisitions added to chart\nSelf-samples provided based on risk factors\nFU with results in app and as questions, concerns, or symptoms arrive`,
    },
    complaints: 'asymptomatic STI screening.\nabsence of STI symptoms, here for routine screening',
    endPage: (
      { patient, primaryPhysician, clinicalDirector, answers },
      onSave,
      buttonsDisabled,
    ) => {
      const partners = (answers['Sexual Hx'] || [])[1] || [];
      const sexWorker = (answers['Sexual Hx'] || [])[7] || [];
      const sexWithFemales = partners[1] || false;
      const isSexWorker = sexWorker[0] || false;

      const physician = primaryPhysician || clinicalDirector;
      return (
        <>
          <div className="templates">
            <span>
              Documents will be added under <b>{PhysicianUtils.getName(physician)}</b>'s name
            </span>
            <PrefilledTemplates
              hideCreateButton
              patient={patient}
              defaultSavedDocumentCategory={DocumentUtils.DOCUMENT_TYPE.diagnostic_requisition}
              physician={physician}
              matchTitles={
                PatientUtils.getGender(patient) === MinistryOHIPGenderEnum.Female
                  ? isSexWorker
                    ? ['STI Female', 'STI Throat and Rectal']
                    : ['STI Female']
                  : sexWithFemales
                  ? ['STI Male', 'STI Throat and Rectal']
                  : ['STI MSM', 'STI Throat and Rectal']
              }
            />
          </div>
          {onSave && (
            <div className="save-button-wrapper">
              <DefaultButton
                name="Save Note"
                className="blue"
                onClick={onSave}
                disabled={buttonsDisabled}
              />
            </div>
          )}
        </>
      );
    },
    pages: ({ healthRecords }) => [
      {
        title: 'Goals & Concerns',
        hideTitle: true,
        questions: [
          {
            label: 'Goals/Concerns',
          },
        ],
      },
      {
        title: 'Medical History',
        hideTitle: true,
        questions: [
          {
            label: 'Allergies',
            defaultAnswer: CPPUtils.getActiveHealthRecords(
              healthRecords?.healthRecords,
              'allergies',
            ).join(', '),
          },
          {
            label: 'Current medical conditions',
            defaultAnswer: CPPUtils.getActiveHealthRecords(
              healthRecords?.healthRecords,
              'medical_issues',
              (value) => value.issue_type === 'Current Issues',
            ).join(', '),
          },
          {
            label: 'Past medical conditions',
            defaultAnswer: CPPUtils.getActiveHealthRecords(
              healthRecords?.healthRecords,
              'medical_issues',
              (value) => value.issue_type === 'Past Issues',
            ).join(', '),
          },
          {
            label: 'Medications',
            questions: [
              {
                input: FLOW_INPUTS.medications,
                hideLabel: true,
              },
            ],
          },
          {
            label: 'Antibiotics in the last 3 months',
          },
        ],
      },
      {
        title: 'Sexual Hx',
        questions: [
          {
            label: '# of partners in the last 6 months',
          },
          {
            label: 'Male/Female/Both',
            noNewLine: true,
            questions: [
              {
                input: FLOW_INPUTS.checkbox,
                label: 'Male',
                noAnswerText: true,
              },
              {
                input: FLOW_INPUTS.checkbox,
                label: 'Female',
                noAnswerText: true,
              },
              {
                input: FLOW_INPUTS.checkbox,
                label: 'Both',
                noAnswerText: true,
              },
            ],
          },
          {
            label: 'Monogamous/non-monogamous',
            noNewLine: true,
            questions: [
              {
                input: FLOW_INPUTS.checkbox,
                label: 'Monogamous',
                noAnswerText: true,
              },
              {
                input: FLOW_INPUTS.checkbox,
                label: 'Non-monogamous',
                noAnswerText: true,
              },
            ],
          },
          {
            label: 'Types of intercourse',
            noNewLine: true,
            questions: [
              {
                input: FLOW_INPUTS.checkbox,
                label: 'Vaginal',
                noAnswerText: true,
              },
              {
                input: FLOW_INPUTS.checkbox,
                label: 'Oral',
                noAnswerText: true,
              },
              {
                input: FLOW_INPUTS.checkbox,
                label: 'Anal receptive',
                noAnswerText: true,
              },
              {
                input: FLOW_INPUTS.checkbox,
                label: 'Anal penetrative',
                noAnswerText: true,
              },
            ],
          },
          {
            label: 'What percentage of the time do you use condoms',
          },
          {
            label: 'Last STI Screen',
          },
          {
            label: 'Any change of pregnancy',
            noNewLine: true,
            filters: {
              gender: 'Female',
            },
            questions: [
              {
                input: FLOW_INPUTS.checkbox,
                label: 'Yes',
                noAnswerText: true,
              },
              {
                input: FLOW_INPUTS.checkbox,
                label: 'No',
                noAnswerText: true,
              },
            ],
          },
          {
            label:
              'Do you identify as a sex worker or do you have intercourse with anyone who identifies as a sex worker',
            noNewLine: true,
            instructions: 'if yes, swab throat and rectal if exposure ',
            questions: [
              {
                input: FLOW_INPUTS.checkbox,
                label: 'Yes',
                noAnswerText: true,
              },
              {
                input: FLOW_INPUTS.checkbox,
                label: 'No',
                noAnswerText: true,
              },
            ],
          },
        ],
      },
    ],
  },
  fm_followup: {
    key: 'fm_followup',
    icon: 'arrows-rotate',
    label: 'FM 45-min Follow-Up',
    includeBeautify: true,
    summary: true,
    pages: () => [
      {
        title: 'Tell the Story',
        description:
          "Plain-language synthesis from the initial consult + returned labs, told back to the patient: here's what's driving things, here's what we found.",
        questions: [
          {
            label: 'Returned labs — what came back',
            instructions: 'interpret functionally',
            noteLabel: 'Returned labs',
            input: FLOW_INPUTS.multiselect,
            inputOptions: {
              options: [
                'Metabolic / insulin',
                'Thyroid',
                'Iron / ferritin',
                'Vit D',
                'B12 / folate',
                'Inflammation',
                'Lipids / ApoB',
                'Hormones',
                'Gut (GI-MAP)',
                'All broadly reassuring',
              ],
            },
          },
          {
            label: 'Key values + functional interpretation',
            instructions: 'one per line',
          },
          {
            label: 'How this connects to their history',
            instructions: 'the thread from the initial timeline → today',
          },
          {
            label: 'The story, in their words',
            instructions: 'root cause → why their symptoms make sense → what we do about it',
          },
          {
            label: 'Top drivers confirmed / revised',
            input: FLOW_INPUTS.multiselect,
            inputOptions: {
              options: ['Confirmed initial impression', 'Revised', 'New finding changes plan'],
            },
          },
          {
            label: 'Top 2–3 drivers now',
            input: FLOW_INPUTS.text,
          },
          {
            label: "Patient's reaction / understanding",
            instructions: 'did it land? what resonated, what worried them',
          },
        ],
      },
      {
        title: 'Initiate the Plan',
        description:
          'Start food & lifestyle now so they leave with momentum. Targeted supplements/Rx can begin today where warranted; the rest is finalized as labs are integrated.',
        questions: [
          { title: 'NUTRITION RX' },
          { label: '1', noteLabel: 'Nutrition Rx 1', input: FLOW_INPUTS.text },
          { label: '2', noteLabel: 'Nutrition Rx 2', input: FLOW_INPUTS.text },
          { title: 'LIFESTYLE RX' },
          { label: 'Sleep' },
          { label: 'Movement' },
          { label: 'Stress' },
          { label: 'Connection / Relationships' },
          { title: 'MEDICATIONS & SUPPLEMENTS' },
          { label: 'Medication adjustments / new Rx' },
          {
            label: 'Supplement bridge',
            instructions: 'begin targeted today; finalize full protocol as labs integrate',
          },
          { title: 'PATIENT EDUCATION PROVIDED' },
          {
            label: 'Covered',
            noteLabel: 'Education covered',
            input: FLOW_INPUTS.multiselect,
            inputOptions: {
              options: ['FM framework overview', 'Fullscript account set up', 'Resources sent'],
            },
          },
          { label: 'Specify resources', input: FLOW_INPUTS.text },
          { title: 'SAFETY / RED FLAGS TO MONITOR' },
          {
            label: 'Red flags to monitor',
            instructions:
              'unexplained weight loss, bleeding, anemia, dysphagia, neuro deficits, oral lesions, severe findings outside scope',
          },
        ],
      },
      {
        title: 'Close & Toolkit',
        description:
          'Close the loop so they leave clear, equipped, and with momentum. Confirm understanding, recap the one most important next step.',
        questions: [
          { title: 'CLOSING THE VISIT' },
          {
            label: 'Done',
            noteLabel: 'Closing',
            input: FLOW_INPUTS.multiselect,
            inputOptions: {
              options: [
                'Teach-back: patient restates plan',
                'Recapped the story (driver → plan)',
                'Confirmed #1 next step',
                'Questions invited & answered',
                'Next visit booked',
              ],
            },
          },
          { label: 'One most important next step' },
          { label: 'Patient\'s "quick win" to do today' },
          { title: 'TOOLKIT ITEMS GIVEN TODAY' },
          {
            label: 'Written plan & results',
            input: FLOW_INPUTS.multiselect,
            inputOptions: { options: ['Executive summary of labs'] },
          },
          {
            label: 'Supplements & prescriptions',
            input: FLOW_INPUTS.multiselect,
            inputOptions: {
              options: [
                'Supplement protocol (doses + timing)',
                'Fullscript link / account',
                'Rx sent to pharmacy',
                'Lab reqs + instructions',
              ],
            },
          },
          {
            label: 'Tracking tools',
            input: FLOW_INPUTS.multiselect,
            inputOptions: {
              options: ['Symptom / food & lifestyle journal', 'Sleep / wearable log', 'BP log'],
            },
          },
          {
            label: 'Education & resources',
            input: FLOW_INPUTS.multiselect,
            inputOptions: {
              options: [
                'Condition-specific handout(s)',
                'Food list / recipes',
                'Book / app / podcast',
                'Other',
              ],
            },
          },
          {
            label: 'Connection & access',
            input: FLOW_INPUTS.multiselect,
            inputOptions: { options: ['How & when to reach clinic', 'Next appointment confirmed'] },
          },
          { label: 'Other items given / notes' },
          { title: 'NEXT VISIT — TRACK: RESULTS REVIEW + EXECUTIVE SUMMARY' },
          {
            label: 'Booked',
            noteLabel: 'Next visit',
            input: FLOW_INPUTS.multiselect,
            inputOptions: { single: true, options: ['In-person', 'Virtual'] },
          },
          { label: 'Date · length', input: FLOW_INPUTS.text },
          {
            label: 'Pre-work for patient',
            input: FLOW_INPUTS.multiselect,
            inputOptions: {
              options: [
                'Complete labs',
                'Diet/lifestyle journal × 7 days',
                'Wearable data',
                'Other',
              ],
            },
          },
        ],
      },
    ],
  },
  fm_initial: {
    key: 'fm_initial',
    icon: 'notes-medical',
    label: 'FM 90-min Initial Consult',
    includeBeautify: true,
    summary: true,
    pages: () => [
      {
        title: 'Chief Concerns & Goals',
        questions: [
          {
            label: 'Current concerns',
            instructions: "patient's words",
            input: FLOW_INPUTS.multiselect,
            inputOptions: {
              options: [
                'Fatigue',
                'Brain fog',
                'Weight',
                'Gut/digestive',
                'Sleep',
                'Mood/anxiety',
                'Pain',
                'Hormonal',
                'Skin',
              ],
            },
          },
          { label: '# · duration · severity /10', input: FLOW_INPUTS.text },
          {
            label: "What's been tried",
            input: FLOW_INPUTS.multiselect,
            inputOptions: {
              options: [
                'Diet change',
                'Supplements',
                'Medication',
                'Naturopath',
                'Specialist',
                'Nothing',
              ],
            },
          },
          { label: "What helped / what didn't" },
          { label: 'What they most want from this visit' },
          { label: 'Ideal state of health', instructions: 'their words' },
        ],
      },
      {
        title: 'Functional Medicine Timeline',
        questions: [
          { title: 'PRENATAL / INFANCY' },
          {
            label: 'Birth method',
            input: FLOW_INPUTS.multiselect,
            inputOptions: { single: true, options: ['Vaginal', 'C-section', 'Unknown'] },
          },
          {
            label: 'Feeding',
            input: FLOW_INPUTS.multiselect,
            inputOptions: { single: true, options: ['Breastfed', 'Formula', 'Mixed'] },
          },
          {
            label: 'Maternal health / pregnancy',
            input: FLOW_INPUTS.multiselect,
            inputOptions: {
              options: ['Complications', 'GDM', 'Smoking/alcohol', 'Stress', 'Unremarkable'],
            },
          },
          { label: 'Details', noteLabel: 'Prenatal details' },
          {
            label: 'Early infections / exposures',
            input: FLOW_INPUTS.multiselect,
            inputOptions: {
              options: ['Frequent infections', 'Early antibiotics', 'Vaccinated on schedule'],
            },
          },
          { label: 'Details', noteLabel: 'Early infection details' },
          { title: 'CHILDHOOD & ADOLESCENCE' },
          {
            label: 'Childhood pattern',
            input: FLOW_INPUTS.multiselect,
            inputOptions: {
              options: [
                'Ear infections',
                'Strep',
                'GI issues',
                'Many Abx courses',
                'Food allergies',
                'Picky eating',
                'ACEs',
              ],
            },
          },
          { label: 'Details', noteLabel: 'Childhood details' },
          {
            label: 'Adolescence',
            input: FLOW_INPUTS.multiselect,
            inputOptions: {
              options: [
                'Early menarche',
                'Late menarche',
                'Acne',
                'OCP use',
                'Accutane',
                'Disordered eating',
                'Body image',
              ],
            },
          },
          { label: 'Menarche age · experience', input: FLOW_INPUTS.text },
          { title: 'ADULTHOOD — MAJOR CHAPTERS' },
          {
            label: 'Key events by decade',
            input: FLOW_INPUTS.multiselect,
            inputOptions: {
              options: [
                '20s',
                '30s',
                '40s',
                'Pregnancies',
                'GDM',
                'Postpartum issues',
                'Major surgery',
                'Hospitalization',
              ],
            },
          },
          { label: 'What happened, when' },
          {
            label: 'Environmental / occupational',
            input: FLOW_INPUTS.multiselect,
            inputOptions: {
              options: ['Mold', 'Water damage', 'Chemicals', 'Heavy metals', 'None known'],
            },
          },
          { label: 'Details', noteLabel: 'Environmental details' },
          { title: 'RECENT TRIGGERS (1–3 YRS)' },
          {
            label: 'Triggers',
            noteLabel: 'Recent triggers',
            instructions: '"My health was never the same since…"',
            input: FLOW_INPUTS.multiselect,
            inputOptions: {
              options: [
                'Infection',
                'COVID',
                'Mono',
                'Major stress',
                'Life transition',
                'Travel',
                'New meds',
                'Grief',
                'Work change',
                'New building / mold',
              ],
            },
          },
          { label: 'Details', noteLabel: 'Recent trigger details' },
        ],
      },
      {
        title: 'ATM Summary',
        questions: [
          {
            label: 'Antecedents',
            instructions: 'predisposing — months to years',
            input: FLOW_INPUTS.multiselect,
            inputOptions: {
              options: [
                'Genetic / family hx',
                'Inherited / acquired',
                'In-utero milieu',
                'Early life exposures',
              ],
            },
          },
          { label: 'Details', noteLabel: 'Antecedent details' },
          {
            label: 'Triggers',
            instructions: 'seconds to weeks',
            input: FLOW_INPUTS.multiselect,
            inputOptions: {
              options: [
                'Foods / allergens',
                'Infections',
                'Trauma / surgery',
                'Stressors',
                'Toxin / med exposure',
              ],
            },
          },
          { label: 'Details', noteLabel: 'Trigger details' },
          {
            label: 'Mediators',
            instructions: 'current, ongoing',
            input: FLOW_INPUTS.multiselect,
            inputOptions: {
              options: [
                'Alcohol',
                'Sugar',
                'Stress',
                'Toxins',
                'Poor sleep',
                'Low movement',
                'Low nutrients',
                'Low connection',
              ],
            },
          },
          { label: 'Excess / deficiency details' },
        ],
      },
      {
        title: 'Five Lifestyle Factors',
        description:
          'From their questionnaire & intake — confirm and correct. Tick what stands out.',
        questions: [
          { title: 'NUTRITION · PFC-MVP + ABCD' },
          {
            label: 'Pattern',
            noteLabel: 'Nutrition',
            input: FLOW_INPUTS.multiselect,
            inputOptions: {
              options: [
                'Adequate protein',
                'Whole carbs',
                'Refined-dominant',
                'Low veg',
                'Ferments daily',
                'High sugar',
                'Skips meals',
                'Sensitivities tried',
              ],
            },
          },
          { label: 'Meals, fast, hydration, caffeine, alcohol details' },
          { title: 'SLEEP' },
          {
            label: 'Pattern',
            noteLabel: 'Sleep',
            input: FLOW_INPUTS.multiselect,
            inputOptions: {
              options: [
                '7+ hrs',
                '<7 hrs',
                'Inconsistent',
                'Trouble falling asleep',
                'Night wakings',
                'Snoring / apnea',
                'Unrefreshed',
                'Sleep aid',
                'Screens at night',
              ],
            },
          },
          { label: 'Bed/wake, wearable data' },
          { title: 'MOVEMENT' },
          {
            label: 'Pattern',
            noteLabel: 'Movement',
            input: FLOW_INPUTS.multiselect,
            inputOptions: {
              options: [
                'Resistance ≥2×',
                'Zone-2 cardio',
                'HIIT',
                'Mostly sedentary',
                'Low steps',
                'Post-workout fatigue',
                'None',
              ],
            },
          },
          { label: 'Type, frequency, steps' },
          { title: 'STRESS · HPA' },
          {
            label: 'Symptoms',
            noteLabel: 'Stress symptoms',
            input: FLOW_INPUTS.multiselect,
            inputOptions: {
              options: ['Tension', 'GI', 'Sleep', 'Mood', 'Energy', 'Libido', 'Cognitive'],
            },
          },
          {
            label: 'Practices',
            noteLabel: 'Stress practices',
            input: FLOW_INPUTS.multiselect,
            inputOptions: {
              options: ['None', 'Breathwork', 'Meditation', 'Yoga', 'Therapy', 'Other'],
            },
          },
          { label: 'HRV trend', input: FLOW_INPUTS.text },
          { title: 'RELATIONSHIPS & CONNECTION' },
          {
            label: 'Pattern',
            noteLabel: 'Connection',
            input: FLOW_INPUTS.multiselect,
            inputOptions: {
              options: [
                'Supportive household',
                'Caregiving load',
                'Socially isolated',
                'Faith/spiritual practice',
                'Community involved',
              ],
            },
          },
          { label: 'Details', noteLabel: 'Connection details' },
        ],
      },
      {
        title: 'Systems Review',
        description: 'Cross-check against MSQ & intake — tick positives, note the abnormal.',
        questions: [
          {
            label: 'Gut / Digestive',
            input: FLOW_INPUTS.multiselect,
            inputOptions: {
              options: [
                'Bloating',
                'Reflux',
                'Pain/cramping',
                'Irregular BM',
                'Food triggers',
                'High Abx burden',
                'Infection hx',
              ],
            },
          },
          { label: 'Details', noteLabel: 'Gut details' },
          {
            label: 'Hormonal — Female / Male',
            input: FLOW_INPUTS.multiselect,
            inputOptions: {
              options: [
                'Irregular cycle',
                'Heavy flow',
                'PMS',
                'Perimenopausal sx',
                'On HRT',
                'Low libido',
                'Body-comp change',
                'On TRT',
              ],
            },
          },
          { label: 'Details', noteLabel: 'Hormonal details' },
          {
            label: 'Thyroid & Metabolic',
            input: FLOW_INPUTS.multiselect,
            inputOptions: {
              options: [
                'Cold intolerance',
                'Hair thinning',
                'Constipation',
                'Weight gain',
                'Fatigue',
                'Known dx',
                'On Rx',
                'FHx T2DM',
              ],
            },
          },
          { label: 'Trajectory, labs date', input: FLOW_INPUTS.text },
          {
            label: 'Cardiovascular',
            input: FLOW_INPUTS.multiselect,
            inputOptions: {
              options: [
                'Elevated BP',
                'FHx early MI/CVA',
                'Dyslipidemia',
                'On statin',
                'Smoker',
                'Known CAC',
              ],
            },
          },
          { label: 'BP, HR, HRV', input: FLOW_INPUTS.text },
          {
            label: 'Neuro · Immune · Derm',
            input: FLOW_INPUTS.multiselect,
            inputOptions: {
              options: [
                'Brain fog',
                'Headaches',
                'Memory',
                'Frequent infections',
                'Allergies',
                'Autoimmune',
                'Joint pain',
                'Skin issues',
                'Hair loss',
              ],
            },
          },
          { label: 'Details', noteLabel: 'Neuro / immune / derm details' },
        ],
      },
      {
        title: 'Physical Exam',
        questions: [
          { title: 'Anthropometrics & seated vitals' },
          { label: 'BP', input: FLOW_INPUTS.text, inline: true },
          { label: 'HR', input: FLOW_INPUTS.text, inline: true },
          { label: 'RR', input: FLOW_INPUTS.text, inline: true },
          { label: 'Wt', input: FLOW_INPUTS.text, inline: true },
          { label: 'Ht', input: FLOW_INPUTS.text, inline: true },
          { label: 'BMI', input: FLOW_INPUTS.text, inline: true },
          { label: 'Waist', input: FLOW_INPUTS.text, inline: true },
          { label: 'Waist/Ht', input: FLOW_INPUTS.text, inline: true },
          { label: 'Waist:Hip', input: FLOW_INPUTS.text, inline: true },
          {
            label: 'Head / hair / eyes',
            input: FLOW_INPUTS.multiselect,
            inputOptions: {
              options: [
                'Normal',
                'Dry/dull hair',
                'Thinning',
                'Premature grey',
                'Conjunctival pallor',
                'Periorbital edema',
              ],
            },
          },
          { label: 'finding → nutrient / node', input: FLOW_INPUTS.text },
          {
            label: 'Mouth / tongue / teeth / neck',
            input: FLOW_INPUTS.multiselect,
            inputOptions: {
              options: [
                'Normal',
                'Atrophic glossitis',
                'Geographic/burning',
                'Fissures',
                'Scalloping',
                'Bleeding gums',
                'Enamel erosion',
                'Thyroid enlarged',
                'Acanthosis',
              ],
            },
          },
          { label: 'Details', noteLabel: 'Mouth / neck details' },
          {
            label: 'Skin / nails / hands',
            input: FLOW_INPUTS.multiselect,
            inputOptions: {
              options: [
                'Normal',
                'Xerosis',
                'Keratosis pilaris',
                'Seborrhea',
                'Petechiae',
                'Skin tags',
                'Rosacea',
                'Koilonychia',
                'Leukonychia',
                "Beau's lines",
                'Brittle',
              ],
            },
          },
          { label: 'Details', noteLabel: 'Skin / nails details' },
          {
            label: 'Chest / CV / resp / abdomen',
            input: FLOW_INPUTS.multiselect,
            inputOptions: {
              options: [
                'Normal',
                'Abnormal breathing',
                'Edema',
                'Distension',
                'Tenderness',
                'Organomegaly',
                'Surgical scars',
              ],
            },
          },
          { label: 'Details', noteLabel: 'Chest / abdomen details' },
          {
            label: 'MSK / neuro / feet',
            input: FLOW_INPUTS.multiselect,
            inputOptions: {
              options: [
                'Normal',
                'Low grip',
                'Slow sit-to-stand',
                'Reduced calf circ',
                'Reduced vibration',
                'Tremor',
                'Neuropathy',
                'Edema',
              ],
            },
          },
          { label: 'Details', noteLabel: 'MSK / neuro details' },
          {
            label: 'Exam synthesis',
            instructions:
              'Top nutrient insufficiencies · hot node(s) · labs to order · handouts given',
          },
        ],
      },
      {
        title: 'Prior Labs Reviewed',
        questions: [
          {
            label: 'Prior labs reviewed',
            instructions: 'Date · Test · Result · Functional interpretation — one per line',
          },
        ],
      },
      {
        title: 'IFM Matrix — Impression',
        questions: [
          { label: 'Primary concern in Matrix language' },
          {
            label: 'Hot nodes',
            instructions: 'tick the active ones',
            input: FLOW_INPUTS.multiselect,
            inputOptions: {
              options: [
                'Assimilation',
                'Defense & Repair',
                'Energy',
                'Biotransformation',
                'Transport',
                'Communication',
                'Structural Integrity',
              ],
            },
          },
          { label: 'Findings · dysfunction · workup per node' },
          {
            label: 'Mental / Emotional / Spiritual',
            input: FLOW_INPUTS.multiselect,
            inputOptions: {
              options: [
                'Cognition',
                'Mood/regulation',
                'Trauma load',
                'Meaning/purpose',
                'Trauma-informed flag',
              ],
            },
          },
          { label: 'Resilience assets' },
          {
            label: 'Node connections',
            input: FLOW_INPUTS.multiselect,
            inputOptions: { options: ['Feedback loop identified', 'Intervention point chosen'] },
          },
          { label: 'Where to break the loop' },
          { label: 'Differential', instructions: 'must-not-miss — rule out…' },
          { label: 'FM synthesis', instructions: 'the story, plain language' },
          {
            label: 'Complexity tier',
            input: FLOW_INPUTS.multiselect,
            inputOptions: {
              single: true,
              options: ['Simple / lifestyle', 'Moderate', 'High / multi-system'],
            },
          },
          { label: 'Top 3 drivers' },
        ],
      },
      {
        title: 'Order — Labs & Referrals',
        questions: [
          { title: `FOUNDATIONAL · ${getHealthCardLabel()} WHERE POSSIBLE` },
          {
            label: 'Foundational labs',
            input: FLOW_INPUTS.multiselect,
            inputOptions: {
              options: [
                'CBC + diff',
                'CMP (GGT)',
                'Lipid + ApoB',
                'Thyroid full',
                'HbA1c + insulin',
                'Vit D',
                'B12 + folate',
                'Ferritin',
                'hsCRP',
                'Homocysteine',
                'Uric acid',
              ],
            },
          },
          { title: 'HORMONAL & ADVANCED · CASE-DEPENDENT' },
          {
            label: 'Advanced labs',
            input: FLOW_INPUTS.multiselect,
            inputOptions: {
              options: [
                'Estradiol/prog',
                'FSH/LH',
                'Testosterone/SHBG',
                'DHEA-S',
                'GI-MAP',
                'Omega-3',
                'RBC Mg',
                'Food sensitivity',
              ],
            },
          },
          { label: 'Other labs / notes' },
          { title: 'REFERRALS' },
          {
            label: 'Refer to',
            input: FLOW_INPUTS.multiselect,
            inputOptions: {
              options: [
                'Dietitian',
                'Pelvic floor PT',
                'Mental health',
                'Cardiology',
                'Endocrine',
                'Sleep med',
                'GI',
                'Gyne/menopause',
                'Derm',
              ],
            },
          },
        ],
      },
      {
        title: 'Close & Toolkit',
        questions: [
          { title: 'CLOSING THE VISIT' },
          {
            label: 'Done',
            noteLabel: 'Closing',
            input: FLOW_INPUTS.multiselect,
            inputOptions: {
              options: [
                'Recapped the story',
                'Teach-back done',
                'Set process & cadence',
                'Questions answered',
              ],
            },
          },
          { label: 'Single most important next step' },
          { title: "TODAY'S STARTING STEPS" },
          {
            label: 'Changes started',
            instructions: 'begin before labs return — 1–2 specific changes',
          },
          { title: 'TOOLKIT ITEMS GIVEN TODAY' },
          {
            label: 'Given',
            noteLabel: 'Toolkit given',
            input: FLOW_INPUTS.multiselect,
            inputOptions: {
              options: [
                'Lab reqs + instructions',
                'Cost sheet',
                'Journal (×7d)',
                'MSQ to repeat',
                'Fullscript set up',
                'Supplement bridge',
                'FM/GOTOIT overview',
                'Handouts / food list',
                'Portal access',
                'How to reach clinic',
              ],
            },
          },
          { title: 'WRAP-UP' },
          { label: 'Safety / red flags to monitor' },
          { label: 'Next visit booked', instructions: 'results review', input: FLOW_INPUTS.text },
          { label: 'Date · length · modality', input: FLOW_INPUTS.text },
        ],
      },
    ],
  },
};

const SendTemplatesToPatient = ({ patient }) => {
  const dispatch = useAppDispatch();
  const templates = useAppSelector((state) => state.templates.templates);
  const [loading, setLoading] = useState(0);

  const TEMPLATES = ['GAD-7', 'PHQ-9'];

  const foundTemplates = useMemo(() => {
    return templates.filter(
      (template) => !!TEMPLATES.find((title) => template.description?.includes(title)),
    );
  }, [templates]);

  async function sendTemplatesToPatient() {
    setLoading(foundTemplates.length);
  }

  async function onSentTemplate(document) {
    if (document) {
      await dispatch(
        api.Documents.markDocumentPendingPatientFill({
          patientId: patient.id,
          documentId: document.id,
          fillAction: 'approve',
        }),
        { errorTitle: 'Send Document to Patient' },
      );
    }
    setLoading((prevState) => prevState - 1);
  }

  return (
    <div className="flex flex-col gap-2 mt-3">
      {loading > 0 &&
        foundTemplates.map(({ id }) => (
          <div style={{ display: 'none' }} key={id}>
            <DocumentTemplate
              templateId={id}
              onClose={() => onSentTemplate()}
              patient={patient}
              onSaveCompleted={(document) => onSentTemplate(document)}
              prefilledValues={undefined}
              autoSave={true}
            />
          </div>
        ))}
      <div className="template-item row gap-4" style={{ alignItems: 'center' }}>
        <DefaultButton
          name="Send GAD-7 and PHQ-9 to Patient"
          loading={loading > 0}
          onClick={() => sendTemplatesToPatient()}
          icon="paper-plane"
          className="blue"
          style={{ width: '280px' }}
        />
      </div>
    </div>
  );
};

function getMatchingImmunizations(immunizations = [], disease, ids) {
  let allowedImmunizations =
    ids || DISEASES.find(({ name }) => name === disease)?.immunizations || [];
  return _.sortBy(
    immunizations.filter(
      ({ value }) => value.given_date && allowedImmunizations.includes(value.name.toLowerCase()),
    ),
    'value.date',
  );
}

function lastImmunizationDate(immunizations = [], disease, ids) {
  let matching = getMatchingImmunizations(immunizations, disease, ids)[0];
  if (matching) {
    return CalendarUtils.format(matching.value.given_date);
  }
}
